import React, { useState, useEffect, ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { HashTag, Member, Poll, PollOption, Post, TribeTemplate } from '../../../../types/entity.types';
import { FileType, RadioOrSelectOption } from '../../../../types/common.types';
import { RootState } from '../../../../types/state/root.types';
import { MemberState } from '../../../../types/state/member.types';
import { TribeState } from '../../../../types/state/tribe.types';
import { SystemState } from '../../../../types/state/system.types';

import { APPROVED, PENDING, POST_SEARCH_OPTIONS, TRIBE_PAGE_SIZE } from '../../../../utils/constants';
import { containsImageOrVideo, generateUUID, isImage } from '../../../../utils/util';

import { requestCreatePost, requestViewPosts, requestUpdatePost } from '../../../../api/post/post.actions';
import { requestCreateComment } from '../../../../api/comment/comment.actions';
import { requestUploadDocuments } from '../../../../api/document/document.actions';
import { requestCreatePoll, requestUpdatePoll } from '../../../../api/poll/poll.actions';
import { requestViewReactions } from '../../../../api/reaction/reaction.actions';
import { requestViewApprovalCount } from '../../../../api/approval/approval.actions';

import { setPost } from '../../../../slices/programme/tribe/tribe.slice';

import TribeInput from '../../../../components/screens/programme/tribe/tribe-input/TribeInput';

type Props = {
  placeholder: string;
  hashtags?: HashTag[];
  comment?: boolean;
  parentExternalReference?: string;
  onPostSuccess: () => void;
  focus?: boolean;
  searchType?: RadioOrSelectOption;
};

const TribeInputContainer = ({
  placeholder,
  hashtags = [],
  comment = false,
  parentExternalReference,
  onPostSuccess,
  focus = false,
  searchType = POST_SEARCH_OPTIONS[0]
}: Props): ReactElement => {
  const [files, setFiles] = useState<File[] | FileType[]>([]);

  const { tribeTemplate, post } = useSelector(({ tribe }: RootState): TribeState => tribe);
  const { members } = useSelector(({ member }: RootState): MemberState => member);
  const { user } = useSelector(({ system }: RootState): SystemState => system);

  const dispatch: Dispatch = useDispatch();

  const { programmeExternalReference } = useParams();

  useEffect((): void => {
    if (post) {
      const { linkedMediaFiles } = post;
      setFiles((linkedMediaFiles as FileType[]) ?? []);
    }
  }, [dispatch, post]);

  const addAttachment = (attachment: File | undefined): void => {
    if (!attachment) return;

    // @ts-expect-error id is being adding to File
    attachment.id = generateUUID();
    if (isImage(attachment.name)) {
      // @ts-expect-error src is being adding to File
      attachment.src = URL.createObjectURL(attachment);
      setFiles([...files, attachment]);
    } else {
      setFiles([...files, attachment]);
    }
  };

  const removeAttachment = (attachment: File | FileType): void => {
    setFiles(
      files.filter((file: File | FileType): boolean => {
        // @ts-expect-error id is added to new files
        if (attachment.id) {
          // @ts-expect-error new files have an id
          return file.id !== attachment.id;
        } else {
          return (file as FileType).documentExternalReference !== (attachment as FileType).documentExternalReference;
        }
      })
    );
  };

  const uploadDocuments = async (): Promise<FileType[]> => {
    return new Promise((resolve: (value: FileType[] | PromiseLike<FileType[]>) => void): void => {
      const documents: FileType[] = [];
      const documentsToUpload: File[] = [];

      for (let file of files) {
        const fileAlreadyUploaded: boolean =
          ('documentExternalReference' in file && !!file.documentExternalReference) || ('link' in file && !!file.link);

        if (fileAlreadyUploaded) {
          if ('link' in file && !!file.link) {
            const beforeIdIndex: number = file.link.indexOf('/videos/');
            const id: string = file.link.substring(beforeIdIndex + 8, file.link.indexOf('?'));

            file = {
              ...file,
              link: `videos/${id}`
            };
          }

          documents.push(file);
        } else {
          documentsToUpload.push(file as File);
        }
      }

      if (documentsToUpload.length > 0) {
        dispatch(
          // @ts-expect-error TODO: Fix after adding types to document actions
          requestUploadDocuments({
            documents: documentsToUpload,
            onSuccess: (uploadedFiles: FileType[]): void => {
              if (uploadedFiles && uploadedFiles.length > 0) {
                documents.splice(0, 0, ...uploadedFiles);
              }
              resolve(documents);
            }
          })
        );
      } else {
        resolve(documents);
      }
    });
  };

  const createPost = async (text: string): Promise<void> => {
    const { approvePosts, approvePhotos } = tribeTemplate as TribeTemplate;

    let approvalStatus: string = APPROVED;
    if (approvePosts) {
      approvalStatus = PENDING;
    } else if (approvePhotos && containsImageOrVideo(files)) {
      approvalStatus = PENDING;
    }

    const linkedMediaFiles: FileType[] = await uploadDocuments();

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to post actions
      requestCreatePost({
        input: {
          text,
          programmeExternalReference,
          approvalStatus,
          linkedMediaFiles
        },
        onSuccess: (): void => {
          dispatch(
            // @ts-expect-error TODO: Fix after adding types to post actions
            requestViewPosts({
              programmeExternalReference,
              page: 0,
              size: TRIBE_PAGE_SIZE,
              searchType: searchType?.value
            })
          );
          // @ts-expect-error TODO: Fix after adding types to approval actions
          dispatch(requestViewApprovalCount({ programmeExternalReference }));
          onPostSuccess();
        }
      })
    );
  };

  const updatePost = async (text: string): Promise<void> => {
    const linkedMediaFiles: FileType[] = await uploadDocuments();

    const { externalReference, pinned } = post as Post;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to post actions
      requestUpdatePost({
        input: {
          postExternalReference: externalReference,
          text,
          linkedMediaFiles,
          pinned
        },
        onSuccess: (): void => {
          dispatch(
            // @ts-expect-error TODO: Fix after adding types to post actions
            requestViewPosts({
              programmeExternalReference,
              page: 0,
              size: TRIBE_PAGE_SIZE,
              searchType: searchType?.value
            })
          );
          dispatch(setPost(undefined));
          // @ts-expect-error TODO: Fix after adding types to approval actions
          dispatch(requestViewApprovalCount({ programmeExternalReference }));
          onPostSuccess();
        }
      })
    );
  };

  const createComment = async (text: string, cleanUp: () => void): Promise<void> => {
    const { approveComments, approvePhotos } = tribeTemplate as TribeTemplate;

    let approvalStatus: string = APPROVED;
    if (approveComments) {
      approvalStatus = PENDING;
    } else if (approvePhotos && containsImageOrVideo(files)) {
      approvalStatus = PENDING;
    }

    const linkedMediaFiles: FileType[] = await uploadDocuments();

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to comment actions
      requestCreateComment({
        input: {
          text,
          parentPostExternalReference: parentExternalReference,
          programmeExternalReference,
          approvalStatus,
          linkedMediaFiles
        },
        onSuccess: (): void => {
          onPostSuccess();
          if (cleanUp) {
            cleanUp();
            setFiles([]);
          }
        }
      })
    );
  };

  const createPoll = async (text: string, poll: Poll): Promise<void> => {
    const { approvePosts, approvePhotos } = tribeTemplate as TribeTemplate;

    let approvalStatus: string = APPROVED;
    if (approvePosts) {
      approvalStatus = PENDING;
    } else if (approvePhotos && containsImageOrVideo(files)) {
      approvalStatus = PENDING;
    }

    const linkedMediaFiles: FileType[] = await uploadDocuments();

    const { question, options, allowMultipleSelection } = poll;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to poll actions
      requestCreatePoll({
        input: {
          text,
          programmeExternalReference,
          approvalStatus,
          linkedMediaFiles,
          question,
          options: options.map((option: PollOption): string => option.value),
          allowMultipleSelection
        },
        onSuccess: (): void => {
          dispatch(
            // @ts-expect-error TODO: Fix after adding types to post actions
            requestViewPosts({
              programmeExternalReference,
              page: 0,
              size: TRIBE_PAGE_SIZE,
              searchType: searchType?.value
            })
          );
          // @ts-expect-error TODO: Fix after adding types to approval actions
          dispatch(requestViewApprovalCount({ programmeExternalReference }));
          onPostSuccess();
        }
      })
    );
  };

  const updatePoll = async (text: string, poll: Poll): Promise<void> => {
    const linkedMediaFiles: FileType[] = await uploadDocuments();

    const { externalReference, pinned } = post as Post;
    const { question, options, allowMultipleSelection } = poll;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to poll actions
      requestUpdatePoll({
        input: {
          pollExternalReference: externalReference,
          text,
          linkedMediaFiles,
          pinned,
          question,
          options: options.map((option: PollOption): string => option.value),
          allowMultipleSelection
        },
        onSuccess: (): void => {
          const { memberExternalReference } = members.find(
            (member: Member): boolean => member.username === user?.username
          ) as Member;
          dispatch(
            // @ts-expect-error TODO: Fix after adding types to post actions
            requestViewPosts({
              programmeExternalReference,
              page: 0,
              size: TRIBE_PAGE_SIZE,
              searchType: searchType?.value
            })
          );
          // @ts-expect-error TODO: Fix after adding types to reaction actions
          dispatch(requestViewReactions({ memberExternalReference }));
          dispatch(setPost(undefined));
          // @ts-expect-error TODO: Fix after adding types to approval actions
          dispatch(requestViewApprovalCount({ programmeExternalReference }));
          onPostSuccess();
        }
      })
    );
  };

  return (
    <TribeInput
      post={post}
      placeholder={placeholder}
      hashtags={hashtags}
      members={members}
      files={files}
      comment={comment}
      addAttachment={addAttachment}
      removeAttachment={removeAttachment}
      createPost={createPost}
      updatePost={updatePost}
      createComment={createComment}
      createPoll={createPoll}
      updatePoll={updatePoll}
      focus={focus}
    />
  );
};

export default TribeInputContainer;
